import {
  Box,
  Button,
  ButtonGroup,
  Fab,
  Grid,
  Pagination,
  styled,
  Typography,
} from '@mui/material';
import LayoutBase from '../../shared/layouts';
import AddIcon from '@mui/icons-material/Add';
import { useEffect, useState } from 'react';
import { api } from '../../shared/services/api';
import Cookies from 'js-cookie';
import UndrawNoData from '../../assets/svg/undraw_no_data.svg';
import { AxiosError, AxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';
import ModalDetail from './components/modalDetails';
import { IResponseCCS0011 } from '../../shared/services/types/response';
import CardCcs0011 from './components/cardCss0011';
import InputSearch from './components/inputSearch';
import Loading from '../../components/loading';

const filtersObject = {
  Todos: 'ALL',
  Criado: 'CREATED',
  Pendente: 'PENDING',
  Deferido: 'DONE',
  Cancelado: 'CANCEL',
  Recusado: 'REFUSED',
};

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const CCS0011 = () => {
  const token = Cookies.get('@token');
  const [ccs0011Data, setCcs0011Data] = useState([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isUploadLoading, setIsUploadLoading] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<IResponseCCS0011 | null>(
    null,
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pages, setPages] = useState<number>(0);
  const [filter, setFilter] = useState<string>('ALL');
  const [cpf, setCpf] = useState<string>('');
  const itemsPerPage = 8;

  // Atualiza a página ao mudar o filtro ou a página
  useEffect(() => {
    handleGetAll();
  }, [currentPage, filter, cpf]);

  const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter);
    setCurrentPage(1); // Reseta a paginação ao alterar o filtro
  };

  const handleGetAll = async () => {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: 'Bearer ' + token,
        'Content-type': 'application/json',
        Accept: 'application/json',
      },
      params: {
        limit: itemsPerPage,
        page: currentPage,
        status: filter !== 'ALL' ? filter : undefined, // Envia filtro apenas se não for "ALL"
        ...(cpf !== '' && { cpf_cnpj_person: cpf }), // Só adiciona cpf_cnpj_person se cpf não for vazio
      },
    };

    try {
      setIsLoading(true);
      const responseFinancialDetail = await api.get(
        '/reportFinancial/getAll',
        config,
      );

      const {
        data,
        meta: { totalPages },
      } = responseFinancialDetail.data;

      setCcs0011Data(data || []);
      setPages(totalPages || 1);
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        const errorMessage = err.response?.data?.error || 'Erro de conexão';
        toast.error(errorMessage);
      } else {
        toast.error('Erro inesperado');
      }
    } finally {
      setIsLoading(false);
    }
    setIsLoading(false);
  };

  const handleFilterCPFChange = (newFilter: string) => {
    // Remove qualquer caractere não numérico (como pontos, traços, barras, etc.)
    const onlyNumbers = newFilter.replace(/\D/g, '');

    setCpf(onlyNumbers); // Define o estado com apenas os números
    setCurrentPage(1); // Reseta a paginação ao alterar o filtro
  };

  const handleClearFilterCPFChange = () => {
    setCpf('');
    setCurrentPage(1); // Reseta a paginação ao alterar o filtro
  };

  const handleUploadCcsArchive = async (files: FileList | null) => {
    setIsUploadLoading(true);

    if (!files || files?.length === 0) {
      setIsUploadLoading(false);
      return;
    }

    if (files[0].type != 'text/xml') {
      toast.warn('Tipo de arquivo inválido');
      setIsUploadLoading(false);
      return;
    }

    const config: AxiosRequestConfig = {
      headers: {
        Authorization: 'Bearer ' + token,
      },
    };

    const formDataBody = new FormData();
    const ccsArchive = files?.[0];

    formDataBody.append('file', ccsArchive);
    try {
      const responseUploadReportFinancial = await api.post(
        '/archive/upload-report-financial',
        formDataBody,
        config,
      );

      if (responseUploadReportFinancial.status === 201) {
        toast.success('Upload feito com sucesso');
      }
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        const errorMessage = err.response?.data?.error || 'Erro de conexão';
        toast.error(errorMessage);
      } else {
        toast.error('Erro inesperado');
      }
    } finally {
      handleGetAll();
      setIsUploadLoading(false);
    }
    setIsUploadLoading(false);
  };

  const handleOpenModal = (item: IResponseCCS0011) => {
    setSelectedItem(item);
  };

  return (
    <LayoutBase
      footer={
        isLoading || ccs0011Data.length === 0 ? null : (
          <Pagination
            count={pages}
            page={currentPage}
            onChange={handleChange}
            color="primary"
          />
        )
      }
    >
      <Typography color="textPrimary" variant="h5">
        CCS0011
      </Typography>
      <Typography sx={{ marginBottom: 2 }}>
        Arquivo CSS0011 (arquivo de detalhe financeiro) : Insira um arquivo
        CCS0011 para que as respostas sejam geradas.{' '}
      </Typography>
      <Grid
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: {
            xs: 'column', // Mobile: empilha em coluna
            sm: 'row', // A partir do "sm": em linha (padrão)
          },
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <Fab
            component="label"
            size="medium"
            color="secondary"
            aria-label="add"
            disabled={isUploadLoading}
          >
            <AddIcon />
            <VisuallyHiddenInput
              type="file"
              onChange={(event) => handleUploadCcsArchive(event.target.files)}
              multiple
            />
          </Fab>

          <ButtonGroup
            variant="outlined"
            color="primary"
            //  sx={{ marginBottom: 2, marginLeft: 2 }}
          >
            {Object.entries(filtersObject).map(([label, value]) => (
              <Button
                key={value}
                variant={filter === value ? 'contained' : 'outlined'}
                onClick={() => handleFilterChange(value)}
              >
                {label}
              </Button>
            ))}
          </ButtonGroup>
        </Box>

        <InputSearch
          label="Pesquise pelo CPF ou CNPJ"
          onSearch={handleFilterCPFChange}
          handleClearInput={handleClearFilterCPFChange}
        />
      </Grid>
      <Loading isLoading={isLoading} />
      {ccs0011Data.length === 0 && !isLoading ? (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Box
              component="img"
              src={UndrawNoData}
              alt="Svg em construção"
              sx={{
                maxWidth: '15%',
                height: 'auto',
                marginTop: 10,
              }}
            />
          </Box>
          <Typography textAlign={'center'} sx={{ marginTop: 2 }}>
            Dados não encontrados...
          </Typography>
        </>
      ) : (
        <Box sx={{ padding: 3 }}>
          <Grid container spacing={2}>
            {ccs0011Data.map((item, index) => (
              <>
                <CardCcs0011
                  key={index}
                  ccsValue={item}
                  handleOpenModal={() => handleOpenModal(item)}
                />
              </>
            ))}
          </Grid>
          <ModalDetail
            open={selectedItem !== null}
            ccsValue={selectedItem}
            closeModal={(shouldReload?: boolean) => {
              setSelectedItem(null);
              if (shouldReload) handleGetAll();
            }}
          />
        </Box>
      )}
    </LayoutBase>
  );
};

export default CCS0011;
